const EU = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
] as const;

export const REGION_PRIORITY = [
  'EU',
  'ASIA',
  'LATAM',
  'AFRICA',
  'MENA',
  'GCC',
  'APAC',
  'NORTH AMERICA',
  'EEA',
  'SEPA',
  'EFTA',
  'GLOBAL',
] as const;

export type RegionCode = (typeof REGION_PRIORITY)[number];

export const REGION_COUNTRIES: Record<RegionCode, readonly string[]> = {
  AFRICA: ['ZA', 'NG', 'KE', 'EG', 'GH'],
  APAC: [
    'AU',
    'NZ',
    'JP',
    'KR',
    'SG',
    'HK',
    'CN',
    'TH',
    'VN',
    'PH',
    'MY',
    'ID',
  ],
  ASIA: [
    'IN',
    'KZ',
    'AZ',
    'JP',
    'KR',
    'SG',
    'HK',
    'CN',
    'TH',
    'VN',
    'PH',
    'MY',
    'ID',
    'TR',
    'IL',
    'MN',
    'PK',
    'BD',
    'LK',
    'AM',
    'GE',
  ],
  EEA: [...EU, 'IS', 'LI', 'NO'],
  EFTA: ['CH', 'IS', 'LI', 'NO'],
  EU: EU,
  GCC: ['AE', 'SA', 'QA', 'KW', 'BH', 'OM'],
  GLOBAL: [],
  LATAM: ['BR', 'MX', 'AR', 'CO', 'CL', 'PE'],
  MENA: ['AE', 'SA', 'EG', 'MA', 'QA', 'KW', 'BH', 'OM', 'JO', 'LB', 'IQ'],
  'NORTH AMERICA': ['US', 'CA'],
  SEPA: [...EU, 'IS', 'LI', 'NO', 'CH', 'GB'],
};

const REGION_LABEL_TO_CODE: Record<string, RegionCode> = {
  africa: 'AFRICA',
  apac: 'APAC',
  asia: 'ASIA',
  eea: 'EEA',
  efta: 'EFTA',
  europe: 'EU',
  gcc: 'GCC',
  global: 'GLOBAL',
  latam: 'LATAM',
  'latin america': 'LATAM',
  mena: 'MENA',
  'north america': 'NORTH AMERICA',
  sepa: 'SEPA',
};

export const REGION_META: Record<RegionCode, { abbrev: string; name: string }> =
  {
    AFRICA: { abbrev: 'AF', name: 'Africa' },
    APAC: { abbrev: 'APAC', name: 'Asia-Pacific' },
    ASIA: { abbrev: 'AS', name: 'Asia' },
    EEA: { abbrev: 'EEA', name: 'European Economic Area' },
    EFTA: { abbrev: 'EFTA', name: 'EFTA' },
    EU: { abbrev: 'EU', name: 'Europe' },
    GCC: { abbrev: 'GCC', name: 'GCC' },
    GLOBAL: { abbrev: 'GL', name: 'Global' },
    LATAM: { abbrev: 'LATAM', name: 'Latin America' },
    MENA: { abbrev: 'MENA', name: 'MENA' },
    'NORTH AMERICA': { abbrev: 'NA', name: 'North America' },
    SEPA: { abbrev: 'SEPA', name: 'SEPA' },
  };

const isRegionCode = (value: string): value is RegionCode =>
  (REGION_PRIORITY as readonly string[]).includes(value);

export const regionLabelToCode = (value: string): RegionCode | undefined => {
  const key = value.trim().toLowerCase();

  if (!key) {
    return undefined;
  }

  const fromLabel = REGION_LABEL_TO_CODE[key];

  if (fromLabel) {
    return fromLabel;
  }

  const upper = value.trim().toUpperCase();

  return isRegionCode(upper) ? upper : undefined;
};

export const countryToRegion = (country: string): RegionCode | undefined => {
  const code = country.trim().toUpperCase();

  return REGION_PRIORITY.find(
    (region) => region !== 'GLOBAL' && REGION_COUNTRIES[region].includes(code),
  );
};

export const regionsForCountries = (
  countries: readonly string[],
): RegionCode[] => {
  const present = new Set<RegionCode>();

  for (const country of countries) {
    const region = countryToRegion(country);

    if (region) {
      present.add(region);
    }
  }

  return REGION_PRIORITY.filter((region) => present.has(region));
};

export const isIsoCountry = (value: string): boolean => {
  const code = value.trim().toUpperCase();

  return /^[A-Z]{2}$/.test(code) && !isRegionCode(code);
};

export const countriesInRegions = (regions: readonly string[]): string[] => {
  const set = new Set<string>();

  for (const region of regions) {
    const code = regionLabelToCode(region);

    if (!code || code === 'GLOBAL') {
      continue;
    }

    for (const country of REGION_COUNTRIES[code]) {
      set.add(country);
    }
  }

  return [...set];
};
